import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts"
import type { PerformanceDrilldownResponse } from "@/lib/api"

const CHART_COLORS = ["#0ca678", "#1971c2", "#e67700", "#c92a2a", "#7048e8", "#495057"]

interface Props {
  data: PerformanceDrilldownResponse
  fmtCurrency: (v: number) => string
}

export function AdrDrilldown({ data, fmtCurrency }: Props) {
  const byChannel = data.adr_by_channel || []
  const byDow = data.adr_by_dow || []
  const distribution = data.adr_distribution || []

  const overallAdr = byChannel.length > 0
    ? byChannel.reduce((sum, c) => sum + c.revenue, 0) / byChannel.reduce((sum, c) => sum + c.bookings, 0) || 0
    : 0
  const minAdr = byChannel.length > 0 ? Math.min(...byChannel.map(c => c.adr)) : 0
  const maxAdr = byChannel.length > 0 ? Math.max(...byChannel.map(c => c.adr)) : 0

  return (
    <div>
      {/* Stat row */}
      <div className="drilldown-stat-row">
        <div className="drilldown-stat">
          <span className="drilldown-stat__label">Overall ADR</span>
          <span className="drilldown-stat__value">{fmtCurrency(overallAdr)}</span>
        </div>
        <div className="drilldown-stat">
          <span className="drilldown-stat__label">Min (by channel)</span>
          <span className="drilldown-stat__value">{fmtCurrency(minAdr)}</span>
        </div>
        <div className="drilldown-stat">
          <span className="drilldown-stat__label">Max (by channel)</span>
          <span className="drilldown-stat__value">{fmtCurrency(maxAdr)}</span>
        </div>
      </div>

      {/* ADR by channel */}
      {byChannel.length > 1 && (
        <div className="drilldown-section">
          <h4 className="drilldown-section__title">ADR by channel</h4>
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
              <BarChart data={byChannel} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--sf-chart-grid, #e9ecef)" vertical={false} />
                <XAxis dataKey="channel" tick={{ fontSize: 11, fill: "var(--sf-chart-text, #868e96)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "var(--sf-chart-text, #868e96)" }} axisLine={false} tickLine={false} tickFormatter={v => fmtCurrency(v)} width={64} />
                <Tooltip contentStyle={{ background: "var(--sf-bg-elevated)", border: "1px solid var(--sf-border)", borderRadius: 8, fontSize: 13 }} formatter={(value: number | undefined) => [fmtCurrency(value ?? 0), "ADR"]} />
                <Bar dataKey="adr" radius={[4, 4, 0, 0]}>
                  {byChannel.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ADR by day of week */}
      {byDow.length > 0 && (
        <div className="drilldown-section">
          <h4 className="drilldown-section__title">ADR by day of week</h4>
          <div style={{ width: "100%", height: 180 }}>
            <ResponsiveContainer>
              <BarChart data={byDow} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--sf-chart-grid, #e9ecef)" vertical={false} />
                <XAxis dataKey="dow" tick={{ fontSize: 11, fill: "var(--sf-chart-text, #868e96)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "var(--sf-chart-text, #868e96)" }} axisLine={false} tickLine={false} tickFormatter={v => fmtCurrency(v)} width={64} />
                <Tooltip contentStyle={{ background: "var(--sf-bg-elevated)", border: "1px solid var(--sf-border)", borderRadius: 8, fontSize: 13 }} formatter={(value: number | undefined) => [fmtCurrency(value ?? 0), "Avg ADR"]} />
                <Bar dataKey="avg_adr" fill="var(--sf-primary)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ADR distribution histogram */}
      {distribution.length > 1 && (
        <div className="drilldown-section">
          <h4 className="drilldown-section__title">ADR distribution</h4>
          <div style={{ width: "100%", height: 180 }}>
            <ResponsiveContainer>
              <BarChart data={distribution} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--sf-chart-grid, #e9ecef)" vertical={false} />
                <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: "var(--sf-chart-text, #868e96)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "var(--sf-chart-text, #868e96)" }} axisLine={false} tickLine={false} width={32} />
                <Tooltip contentStyle={{ background: "var(--sf-bg-elevated)", border: "1px solid var(--sf-border)", borderRadius: 8, fontSize: 13 }} formatter={(value: number | undefined) => [value ?? 0, "Bookings"]} />
                <Bar dataKey="count" fill="#1971c2" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  )
}
